// Employee Profile — header/details card.
// People Hub > My Team > {Employee Name}
import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { MyTeamMember } from '../types';
import { EmployeeProfileDetails } from '../types';

interface EmployeeDetailsCardProps {
  member: MyTeamMember;
  details: EmployeeProfileDetails;
  onEdit?: () => void;
  onBeginActivity?: () => void;
}

const initialsFor = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase())
    .join('');

const EmployeeDetailsCard: React.FC<EmployeeDetailsCardProps> = ({ member, details, onEdit, onBeginActivity }) => {
  const fields: { label: string; value: string }[] = [
    { label: 'Manager', value: details.manager },
    { label: 'Level', value: details.level },
    { label: 'Organization', value: details.organization },
    { label: 'Location / Shift', value: `${details.location} / ${details.shift}` },
  ];
  const fields2: { label: string; value: string }[] = [
    { label: 'Start Date', value: details.startDate },
    { label: 'Service', value: details.service },
    { label: 'Current Period', value: details.currentPeriod },
  ];

  return (
    <View style={styles.card}>
      <View style={styles.leftBlock}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{initialsFor(member.name)}</Text>
        </View>
        <View style={styles.identity}>
          <Text style={styles.name} numberOfLines={1}>{member.name}</Text>
          <Text style={styles.role} numberOfLines={1}>{member.role}</Text>
          <View style={styles.badgeRow}>
            <View style={styles.statusBadge}>
              <Text style={styles.statusBadgeText}>{member.status}</Text>
            </View>
            <View style={styles.gpidBadge}>
              <Text style={styles.gpidBadgeText}>GPID {member.gpid}</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.divider} />

      <View style={styles.middleBlock}>
        <View style={styles.fieldRow}>
          {fields.map(f => (
            <View key={f.label} style={styles.fieldCol}>
              <Text style={styles.fieldLabel}>{f.label}</Text>
              <Text style={styles.fieldValue} numberOfLines={1}>{f.value}</Text>
            </View>
          ))}
        </View>
        <View style={styles.fieldRow}>
          {fields2.map(f => (
            <View key={f.label} style={styles.fieldCol}>
              <Text style={styles.fieldLabel}>{f.label}</Text>
              <Text style={styles.fieldValue} numberOfLines={1}>{f.value}</Text>
            </View>
          ))}
          <View style={styles.fieldCol} />
        </View>
      </View>

      <View style={styles.actionsBlock}>
        <Pressable style={styles.secondaryButton} onPress={onEdit}>
          <Text style={styles.secondaryButtonText}>Edit</Text>
        </Pressable>
        <Pressable style={styles.primaryButton} onPress={onBeginActivity}>
          <Text style={styles.primaryButtonText}>Begin activity</Text>
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    paddingVertical: 16,
    paddingHorizontal: 20,
    gap: 20,
  },
  leftBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minWidth: 220,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: AdminColors.blue75,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    color: AdminColors.blue700,
  },
  identity: {
    gap: 2,
  },
  name: {
    fontFamily: FontFamily.bold,
    fontSize: 18,
    color: AdminColors.textPrimary,
  },
  role: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textLink,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 4,
  },
  statusBadge: {
    backgroundColor: AdminColors.green75,
    borderRadius: 10,
    paddingVertical: 2,
    paddingHorizontal: 8,
  },
  statusBadgeText: {
    fontFamily: FontFamily.bold,
    fontSize: 11,
    color: AdminColors.green600,
  },
  gpidBadge: {
    backgroundColor: AdminColors.gray25,
    borderRadius: 10,
    paddingVertical: 2,
    paddingHorizontal: 8,
  },
  gpidBadgeText: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textSecondary,
  },
  divider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: AdminColors.borderDefault,
  },
  middleBlock: {
    flex: 1,
    gap: 10,
  },
  fieldRow: {
    flexDirection: 'row',
  },
  fieldCol: {
    flex: 1,
    gap: 2,
  },
  fieldLabel: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
  },
  fieldValue: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  actionsBlock: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  secondaryButton: {
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    paddingVertical: 9,
    paddingHorizontal: 16,
  },
  secondaryButtonText: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  primaryButton: {
    backgroundColor: AdminColors.blue700,
    borderRadius: 6,
    paddingVertical: 9,
    paddingHorizontal: 16,
  },
  primaryButtonText: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: '#FFFFFF',
  },
});

export default EmployeeDetailsCard;
